import React, { useEffect, useState, useCallback, useRef, forwardRef } from "react";
import { Loader2, CheckCircle2, Handshake, Upload, Trash2, AlertCircle } from "lucide-react";
import { publicClient } from "@/lib/apiClient";
import { isImageFile, formatBytes } from "@/lib/imageUtils";

const FILE_MAX_BYTES = 3 * 1024 * 1024;
const MAX_FILES = 5;

const INITIAL = {
  listing_id: "",
  project_title: "",
  company_name: "",
  contact_name: "",
  phone: "",
  email: "",
  website_or_social: "",
  trade_specialty: "",
  service_areas: "",
  crew_size: "",
  years_in_business: "",
  insurance_status: "",
  license_status: "",
  availability: "",
  can_attend_site_walk: null,
  can_provide_labor_only: null,
  can_provide_labor_and_materials: null,
  references: "",
  message: "",
  files: [],
  website: "",
};

const YES_NO_OPTIONS = [
  { label: "Yes", value: true },
  { label: "No", value: false },
  { label: "N/A", value: null },
];

function YesNoToggle({ value, onChange, name }) {
  return (
    <div className="flex gap-1" data-testid={`yesno-${name}`}>
      {YES_NO_OPTIONS.map((opt) => {
        const isActive = value === opt.value;
        return (
          <button
            key={opt.label}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`px-3 py-1 rounded text-[11px] uppercase tracking-widest font-semibold border transition ${
              isActive
                ? "bg-br-bronze border-br-bronze text-white"
                : "bg-white border-gray-200 text-gray-600 hover:border-br-bronze"
            }`}
            data-testid={`yesno-${name}-${opt.label.toLowerCase()}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

/**
 * PartnerApplicationForm
 *
 * Submits to POST /api/project-listings/apply with up to 5 base64-embedded
 * documents (insurance, license, portfolio). The parent page passes
 * `prefill` to populate listing_id + project_title from a chosen listing.
 */
const PartnerApplicationForm = forwardRef(function PartnerApplicationForm({ prefill }, ref) {
  const [form, setForm] = useState(INITIAL);
  const [state, setState] = useState({ loading: false, success: false, error: "" });
  const inputRef = useRef(null);

  useEffect(() => {
    if (!prefill) return;
    setForm((prev) => ({
      ...prev,
      ...(prefill.listing_id ? { listing_id: prefill.listing_id } : {}),
      ...(prefill.project_title ? { project_title: prefill.project_title } : {}),
    }));
    setState((s) => (s.success ? { loading: false, success: false, error: "" } : s));
  }, [prefill]);

  const update = useCallback((k) => (e) => {
    const v = e?.target?.value ?? e;
    setForm((p) => ({ ...p, [k]: v }));
  }, []);

  const handleFiles = useCallback(async (e) => {
    const incoming = Array.from(e.target.files || []);
    if (incoming.length === 0) return;
    const remaining = MAX_FILES - form.files.length;
    const accepted = incoming.slice(0, remaining);
    const errors = [];
    const next = [...form.files];
    for (const f of accepted) {
      if (f.size > FILE_MAX_BYTES) {
        errors.push(`${f.name}: exceeds 3MB.`);
        continue;
      }
      try {
        const dataUrl = await readAsDataUrl(f);
        next.push({
          filename: f.name,
          content_type: f.type || "application/octet-stream",
          size: f.size,
          data_url: dataUrl,
          label: isImageFile(f) ? "portfolio" : "document",
        });
      } catch {
        errors.push(`${f.name}: failed to read.`);
      }
    }
    setForm((p) => ({ ...p, files: next }));
    if (errors.length) setState({ loading: false, success: false, error: errors.join(" ") });
    if (inputRef.current) inputRef.current.value = "";
  }, [form.files]);

  const removeFile = (idx) => setForm((p) => ({ ...p, files: p.files.filter((_, i) => i !== idx) }));

  const submit = useCallback(async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.phone || !form.email) {
      setState({ loading: false, success: false, error: "Company, contact name, phone, and email are required." });
      return;
    }
    setState({ loading: true, success: false, error: "" });
    try {
      await publicClient.post("/project-listings/apply", form);
      setState({ loading: false, success: true, error: "" });
      setForm((p) => ({ ...INITIAL, listing_id: p.listing_id, project_title: p.project_title }));
    } catch (err) {
      setState({
        loading: false,
        success: false,
        error: err?.response?.data?.detail || "Something went wrong, please try again.",
      });
    }
  }, [form]);

  if (state.success) {
    return (
      <div ref={ref} id="partner-apply" className="bg-white border border-gray-200 rounded-lg p-6 scroll-mt-24" data-testid="partner-application-form">
        <div className="flex items-start gap-3 bg-br-paper border border-br-bronze rounded p-5" data-testid="partner-application-success">
          <CheckCircle2 size={22} className="text-br-bronze shrink-0 mt-0.5" />
          <div>
            <div className="font-display tracking-widest uppercase text-lg text-br-black">Submission Received</div>
            <p className="text-sm text-gray-700 mt-1 leading-relaxed">
              Thank you for submitting your information for this project opportunity. Black Ridge Construction received your application and will review it before reaching out.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} id="partner-apply" className="bg-white border border-gray-200 rounded-lg overflow-hidden scroll-mt-24" data-testid="partner-application-form">
      <div className="px-6 py-5 border-b border-gray-200 bg-br-paper flex items-start gap-3">
        <Handshake size={20} className="text-br-bronze shrink-0 mt-1" />
        <div>
          <div className="br-eyebrow text-br-bronze">Apply as a Trade Partner</div>
          <h3 className="font-display text-2xl tracking-widest uppercase mt-1">
            {form.project_title ? `Submit for ${form.project_title}` : "Submit Your Trade Partner Info"}
          </h3>
          <p className="text-sm text-gray-700 mt-2 max-w-2xl">
            Send us your company info, coverage, and required documents. Black Ridge will review and follow up with shortlisted partners.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="p-6 grid sm:grid-cols-2 gap-4" data-testid="partner-form-fields">
        <div>
          <label className="label-br" htmlFor="paf-company">Company Name *</label>
          <input id="paf-company" className="input-br" value={form.company_name} onChange={update("company_name")} required data-testid="paf-company" />
        </div>
        <div>
          <label className="label-br" htmlFor="paf-contact">Contact Name *</label>
          <input id="paf-contact" className="input-br" value={form.contact_name} onChange={update("contact_name")} required data-testid="paf-contact" />
        </div>
        <div>
          <label className="label-br" htmlFor="paf-phone">Phone *</label>
          <input id="paf-phone" type="tel" className="input-br" value={form.phone} onChange={update("phone")} required data-testid="paf-phone" />
        </div>
        <div>
          <label className="label-br" htmlFor="paf-email">Email *</label>
          <input id="paf-email" type="email" className="input-br" value={form.email} onChange={update("email")} required data-testid="paf-email" />
        </div>
        <div className="sm:col-span-2">
          <label className="label-br" htmlFor="paf-website">Website or Social</label>
          <input id="paf-website" className="input-br" value={form.website_or_social} onChange={update("website_or_social")} placeholder="https:// or @handle" data-testid="paf-website-social" />
        </div>
        <div>
          <label className="label-br" htmlFor="paf-trade">Trade / Specialty</label>
          <input id="paf-trade" className="input-br" value={form.trade_specialty} onChange={update("trade_specialty")} placeholder="Drywall + Paint, Roofing, HVAC…" data-testid="paf-trade" />
        </div>
        <div>
          <label className="label-br" htmlFor="paf-areas">Service Areas Covered</label>
          <input id="paf-areas" className="input-br" value={form.service_areas} onChange={update("service_areas")} placeholder="Austin, Round Rock, Cedar Park…" data-testid="paf-areas" />
        </div>
        <div>
          <label className="label-br" htmlFor="paf-crew">Crew Size</label>
          <input id="paf-crew" className="input-br" value={form.crew_size} onChange={update("crew_size")} placeholder="e.g. 2-4 / 5+" data-testid="paf-crew" />
        </div>
        <div>
          <label className="label-br" htmlFor="paf-years">Years in Business</label>
          <input id="paf-years" className="input-br" value={form.years_in_business} onChange={update("years_in_business")} data-testid="paf-years" />
        </div>
        <div>
          <label className="label-br" htmlFor="paf-insurance">Insurance Status</label>
          <input id="paf-insurance" className="input-br" value={form.insurance_status} onChange={update("insurance_status")} placeholder="Active GL / WC…" data-testid="paf-insurance" />
        </div>
        <div>
          <label className="label-br" htmlFor="paf-license">License / Certification</label>
          <input id="paf-license" className="input-br" value={form.license_status} onChange={update("license_status")} placeholder="If applicable" data-testid="paf-license" />
        </div>
        <div className="sm:col-span-2">
          <label className="label-br" htmlFor="paf-avail">Availability</label>
          <input id="paf-avail" className="input-br" value={form.availability} onChange={update("availability")} placeholder="Immediate / 2-week start / etc." data-testid="paf-availability" />
        </div>

        {/* Yes/No qualifiers */}
        <div className="sm:col-span-2 grid sm:grid-cols-3 gap-3 border-t border-gray-100 pt-4">
          {[
            ["can_attend_site_walk", "Can attend a site walk?"],
            ["can_provide_labor_only", "Can provide labor only?"],
            ["can_provide_labor_and_materials", "Can provide labor + materials?"],
          ].map(([key, label]) => (
            <div key={key} className="bg-br-paper/40 border border-gray-100 rounded px-3 py-2">
              <div className="text-xs text-gray-700 leading-snug mb-1">{label}</div>
              <YesNoToggle name={key} value={form[key]} onChange={(v) => setForm((p) => ({ ...p, [key]: v }))} />
            </div>
          ))}
        </div>

        <div className="sm:col-span-2">
          <label className="label-br" htmlFor="paf-message">Message / Notes</label>
          <textarea id="paf-message" rows={3} className="textarea-br" value={form.message} onChange={update("message")} placeholder="Anything Black Ridge should know about your fit for this scope." data-testid="paf-message" />
        </div>
        <div className="sm:col-span-2">
          <label className="label-br" htmlFor="paf-refs">References (optional)</label>
          <textarea id="paf-refs" rows={2} className="textarea-br" value={form.references} onChange={update("references")} placeholder="Names + phone numbers or links" data-testid="paf-refs" />
        </div>

        {/* Uploads */}
        <div className="sm:col-span-2">
          <div className="label-br mb-1">Upload Insurance, License, or Portfolio Documents</div>
          <div className="border-2 border-dashed border-gray-300 rounded-lg bg-br-paper/40 p-5">
            <div className="flex items-center gap-3">
              <Upload size={20} className="text-br-bronze" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-gray-900">Drop or browse files</div>
                <div className="text-[11px] text-gray-500">Up to {MAX_FILES} files, 3MB each. PDFs or images preferred.</div>
              </div>
              <button type="button" onClick={() => inputRef.current?.click()} className="br-btn-dark text-[11px]" disabled={form.files.length >= MAX_FILES} data-testid="paf-file-pick">
                Browse
              </button>
              <input ref={inputRef} type="file" multiple className="hidden" onChange={handleFiles} accept=".pdf,image/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" data-testid="paf-file-input" />
            </div>
            {form.files.length > 0 && (
              <ul className="mt-3 grid sm:grid-cols-2 gap-2" data-testid="paf-file-list">
                {form.files.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-gray-700 bg-white border border-gray-100 rounded px-2 py-1.5">
                    <Handshake size={12} className="text-br-bronze shrink-0" />
                    <span className="truncate flex-1">{f.filename}</span>
                    <span className="text-[10px] text-gray-400">{formatBytes(f.size)}</span>
                    <button type="button" onClick={() => removeFile(i)} aria-label="Remove" className="text-red-500 hover:text-red-700">
                      <Trash2 size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Honeypot */}
        <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={update("website")} className="hidden" aria-hidden="true" />

        {state.error && (
          <div className="sm:col-span-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3 flex items-start gap-2" data-testid="paf-error">
            <AlertCircle size={14} className="mt-0.5 shrink-0" /> {state.error}
          </div>
        )}

        <div className="sm:col-span-2 flex flex-wrap items-center gap-3 pt-1">
          <button type="submit" disabled={state.loading} className="br-btn-primary" data-testid="paf-submit">
            {state.loading ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : "Submit for This Scope"}
          </button>
          <span className="text-[11px] text-gray-500">Submissions land in the Black Ridge admin dashboard.</span>
        </div>
      </form>
    </div>
  );
});

export default PartnerApplicationForm;
